import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useOrganization } from '../contexts/OrganizationContext'
import { tagApi } from '../services/api'
import TagForm from '../components/TagForm'
import { EMPTY_TAG, getTagStyles, getIconComponent } from '../components/tagUtils'
import { FONT_STYLE_OPTIONS, POSITION_OPTIONS, optionLabel } from '../components/tagFormOptions'
import {
  Button,
  ConfirmDialog,
  DataTable,
  PageHeader,
  PageLayout,
  StatePanel,
  StatusBadge,
} from '../components/common'
import styles from './TagsPage.module.css'

function TagsPage() {
  const { currentOrg } = useOrganization()
  const queryClient = useQueryClient()
  const [editingTag, setEditingTag] = useState(null) // null | 'new' | tag id
  const [editInitialData, setEditInitialData] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tags', currentOrg?.slug],
    queryFn: () => tagApi.list(currentOrg.slug),
    enabled: !!currentOrg?.slug,
  })

  const tags = data?.tags || []

  const openCreate = () => {
    setEditInitialData(EMPTY_TAG)
    setEditingTag('new')
  }

  const openEdit = (tag) => {
    setEditInitialData({
      label: tag.label,
      bg_color: tag.bg_color,
      text_color: tag.text_color,
      icon: tag.icon || '',
      font_style: tag.font_style,
      position: tag.position,
      sort_order: tag.sort_order,
    })
    setEditingTag(tag.id)
  }

  const handleSave = async (formData) => {
    if (editingTag === 'new') {
      await tagApi.create(currentOrg.slug, formData)
      toast.success('Tag created')
    } else {
      await tagApi.update(currentOrg.slug, editingTag, formData)
      toast.success('Tag updated')
    }
    queryClient.invalidateQueries({ queryKey: ['tags', currentOrg?.slug] })
  }

  const handleDelete = async () => {
    if (!pendingDelete || deleting) return
    setDeleting(true)
    try {
      await tagApi.delete(currentOrg.slug, pendingDelete.id)
      queryClient.invalidateQueries({ queryKey: ['tags', currentOrg?.slug] })
      toast.success('Tag deactivated')
      setPendingDelete(null)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setDeleting(false)
    }
  }

  const columns = [
    {
      key: 'label',
      label: 'Tag',
      sticky: 'start',
      sortable: true,
      priority: 'primary',
      render: (tag) => {
        const RowIcon = getIconComponent(tag.icon)
        return (
          <span className={styles.tagPreview}>
            <span className={styles.tagPalette} aria-hidden="true">
              <span style={{ backgroundColor: tag.bg_color }} />
              <span style={{ backgroundColor: tag.text_color }} />
            </span>
            <span
              className={styles.tagLabel}
              style={{
                ...getTagStyles(tag.font_style),
              }}
            >
              {RowIcon ? <RowIcon size={12} aria-hidden="true" /> : null}
              {tag.label}
            </span>
          </span>
        )
      },
    },
    { key: 'position', label: 'Position', sortable: true, priority: 'secondary', render: (tag) => optionLabel(POSITION_OPTIONS, tag.position) },
    { key: 'font_style', label: 'Style', sortable: true, priority: 'tertiary', render: (tag) => optionLabel(FONT_STYLE_OPTIONS, tag.font_style) },
    {
      key: 'icon',
      label: 'Icon',
      priority: 'tertiary',
      sortable: true,
      render: (tag) => {
        const RowIcon = getIconComponent(tag.icon)
        return RowIcon ? (
          <span className={styles.iconCell}><RowIcon size={14} aria-hidden="true" />{tag.icon}</span>
        ) : 'None'
      },
    },
    { key: 'sort_order', label: 'Order', sortable: true, numeric: true, priority: 'secondary' },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      render: (tag) => (
        <StatusBadge status={tag.is_active ? 'active' : 'inactive'} size="small">
          {tag.is_active ? 'Active' : 'Inactive'}
        </StatusBadge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sticky: 'end',
      priority: 'primary',
      render: (tag) => (
        <span className={styles.actions}>
          <Button variant="ghost" size="compact" onClick={() => openEdit(tag)}>Edit</Button>
          {tag.is_active ? (
            <Button variant="danger" size="compact" onClick={() => setPendingDelete(tag)}>
              Deactivate
            </Button>
          ) : null}
        </span>
      ),
    },
  ]

  if (!currentOrg?.slug) {
    return (
      <PageLayout size="wide">
        <PageHeader title="Tags" />
        <StatePanel
          state="restricted"
          title="No organization selected"
          description="Pick an organization to manage its tags."
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Tags"
        primaryAction={<Button onClick={openCreate}>Create tag</Button>}
      />

      {isLoading ? (
        <StatePanel state="loading" title="Loading tags…" />
      ) : isError ? (
        <StatePanel
          state="error"
          title="Tags could not load"
          description="Your existing tags were not changed."
          onRetry={refetch}
        />
      ) : tags.length === 0 ? (
        <StatePanel
          state="empty"
          title="No tags yet"
          description="Create a tag to label feed content."
        />
      ) : (
        <DataTable
          label="Tags"
          data={tags}
          columns={columns}
          density="compact"
          keyField="id"
          responsiveColumns
        />
      )}

      {editingTag && (
        <TagForm
          initialData={editInitialData}
          title={editingTag === 'new' ? 'Create Tag' : 'Edit Tag'}
          saveLabel={editingTag === 'new' ? 'Create' : 'Save'}
          onSave={handleSave}
          onClose={() => setEditingTag(null)}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Deactivate tag"
        message={pendingDelete ? `Deactivate "${pendingDelete.label}"? It will be hidden from selection.` : ''}
        variant="danger"
        confirmLabel={deleting ? 'Deactivating…' : 'Deactivate'}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </PageLayout>
  )
}

export default TagsPage
